import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Grid, Chip, CircularProgress, TablePagination, Card, CardContent, useTheme, alpha, Tooltip, Stack, Snackbar, Popover, RadioGroup, FormControlLabel, Radio, Alert } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CalendarMonth as CalendarMonthIcon, LocationOn as LocationOnIcon, Public as PublicIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { enGB } from 'date-fns/locale';
import dayjs from 'dayjs';
import { getData, postData, putData, deleteData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import Loader from '../../../common/spinner';

function HolidayCalendarManager() {
    const theme = useTheme();
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData?.organization_id;
    const userLocationId = userData?.location_id;
    const [holidays, setHolidays] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedLocation, setSelectedLocation] = useState('All');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentHoliday, setCurrentHoliday] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectDownloadFormat, setSelectDownloadFormat] = useState('pdf');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const years = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 3 + i);

    const showToast = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.fetchLocationsByOrganizationId(orgId));
            if (response.status === "SUCCESS") {
                setLocations(response.message || []);
                setSelectedLocation('All');
            }
        } catch (err) {
            showToast("Failed to load locations", "error");
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    const fetchHolidays = useCallback(async () => {
        setLoading(true);
        try {
            let filterLocId = userLocationId || 1;
            if (selectedLocation !== 'All') {
                const locObj = locations.find(l => l.location_name === selectedLocation);
                filterLocId = locObj ? locObj.id : 1;
            }

            const response = await getData(API_ENDPOINTS.getHolidaysByYearAndMonth(selectedYear, orgId, filterLocId));

            if (response.status === "SUCCESS") {
                const rawHolidays = response.message.holidays || [];
                const formatted = rawHolidays.map(h => {
                    const applicableNames = locations
                        .filter(loc => h.location_ids?.includes(loc.id))
                        .map(loc => loc.location_name);

                    return {
                        id: h.id,
                        name: h.holiday_name,
                        date: h.date ? new Date(h.date) : new Date(),
                        is_national: h.is_national,
                        description: h.description || '',
                        location_ids: h.location_ids || [],
                        applicableLocations: h.is_national ? ['All'] : (applicableNames.length > 0 ? applicableNames : ['All'])
                    };
                });
                setHolidays(formatted);
            }
        } catch (err) {
            showToast("Failed to load holidays", "error");
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedLocation, orgId, locations, userLocationId]);
    useEffect(() => {
        if (orgId) fetchLocations();
    }, [fetchLocations, orgId]);

    useEffect(() => {
        if (orgId && locations.length > 0) fetchHolidays();
    }, [fetchHolidays, orgId, locations.length]);

    // --- Actions ---

    const handleOpenDialog = (holiday = null) => {
        if (holiday) {
            setCurrentHoliday({ ...holiday });
            setEditMode(true);
        } else {
            setCurrentHoliday({
                name: '',
                date: new Date(),
                description: '',
                is_national: true,
                applicableLocations: ['All'],
                location_ids: locations.map(l => l.id)
            });
            setEditMode(false);
        }
        setDialogOpen(true);
    };

    const handleLocationChange = (e) => {
        const val = e.target.value;
        let newSelections = typeof val === 'string' ? val.split(',') : val;
        setPage(0);
        // Logic for handling "All" toggle
        if (newSelections.includes('All') && !currentHoliday.applicableLocations.includes('All')) {
            newSelections = ['All'];
        } else if (newSelections.length > 1 && newSelections.includes('All')) {
            newSelections = newSelections.filter(x => x !== 'All');
        }

        const locIds = newSelections.includes('All')
            ? locations.map(l => l.id)
            : locations.filter(l => newSelections.includes(l.location_name)).map(l => l.id);

        setCurrentHoliday(prev => ({
            ...prev,
            applicableLocations: newSelections,
            location_ids: locIds,
            is_national: newSelections.includes('All')
        }));
    };

    const handleSaveHoliday = async () => {
        if (!currentHoliday.name.trim()) return showToast("Name is required", "error");

        setLoading(true);
        const payload = {
            holiday_name: currentHoliday.name,
            date: dayjs(currentHoliday.date).format('YYYY-MM-DD'),
            organization_id: parseInt(orgId),
            location_ids: currentHoliday.location_ids,
            is_national: currentHoliday.is_national,
            description: currentHoliday.description
        };

        try {
            let res;
            if (editMode) {
                res = await putData(API_ENDPOINTS.updateHolidayById(currentHoliday.id), payload);
            } else {
                res = await postData(API_ENDPOINTS.createHoliday, payload);
            }

            if (res.status === "SUCCESS" || res.statusCode === 200 || res.statusCode === 201) {
                showToast(`Holiday ${editMode ? 'updated' : 'created'} successfully`);
                fetchHolidays();
                setDialogOpen(false);
            } else {
                showToast(res.message || "Operation failed", "error");
            }
        } catch (err) {
            showToast("Server error occurred", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        try {
            const res = await deleteData(API_ENDPOINTS.deleteHolidayById(holidayToDelete.id));
            if (res.status === "SUCCESS") {
                showToast("Holiday deleted");
                fetchHolidays();
            }
        } catch (err) {
            showToast("Delete failed", "error");
        } finally {
            setLoading(false);
            setDeleteDialogOpen(false);
        }
    };

    const handleDownloadCalendar = async () => {
        try {
            const isExcel = selectDownloadFormat === 'excel';
            const ext = isExcel ? 'xlsx' : 'pdf';

            const finalUrl = isExcel
                ? API_ENDPOINTS.downloadHolidayListExcel(selectedYear, orgId, userLocationId)
                : API_ENDPOINTS.downloadHolidayListPDF(selectedYear, orgId, userLocationId);
            const response = await getData(finalUrl, { responseType: 'blob' });

            const blob = new Blob([response], {
                type: isExcel
                    ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    : "application/pdf"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute('download', `Holiday_List_${selectedYear}.${ext}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setAnchorEl(null);
            showToast("Download started successfully");
        } catch (error) {
            showToast("Download failed", "error");
        }
    };

    const formatDateString = (date) => dayjs(date).format('DD/MM/YYYY');
    const getDayName = (date) => dayjs(date).format('dddd');

    const sortedHolidays = [...holidays].sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());
    const paginatedHolidays = sortedHolidays.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.background.default, 0.5), minHeight: '100vh' }}>
            {loading && <Loader />}
            <Card elevation={0} sx={{ mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)', color: 'white' }}>
                <CardContent sx={{ py: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CalendarMonthIcon sx={{ fontSize: 40 }} />
                            <Box>
                                <Typography variant="h5" fontWeight={600}>
                                    Holiday Calendar
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Manage organization-wide and regional holidays
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                whiteSpace: 'nowrap',
                                textTransform: 'none',
                            }}
                        >
                            Add Holiday
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Filters Card */}
            <Card elevation={0} sx={{ mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ py: 2 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
                                    {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Location Filter</InputLabel>
                                <Select value={selectedLocation} label="Location Filter" onChange={(e) => setSelectedLocation(e.target.value)}>
                                    <MenuItem value="All">All Locations</MenuItem>
                                    {locations.map(loc => (
                                        <MenuItem key={loc.id} value={loc.location_name}>{loc.location_name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'right' }}>
                            <Tooltip title="Download Calendar">
                                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                    <CloudDownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Table Card */}
            <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Day</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Holiday Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Locations</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
                            ) : paginatedHolidays.length > 0 ? (
                                paginatedHolidays.map((holiday) => (
                                    <TableRow key={holiday.id} hover>
                                        <TableCell>{formatDateString(holiday.date)}</TableCell>
                                        <TableCell>
                                            <Chip label={getDayName(holiday.date)} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>{holiday.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{holiday.description}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                icon={holiday.is_national ? <PublicIcon /> : <LocationOnIcon />}
                                                label={holiday.is_national ? "National" : "Regional"}
                                                color={holiday.is_national ? "primary" : "info"}
                                                variant="light"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {holiday.applicableLocations.map(loc => (
                                                    <Chip key={loc} label={loc} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(holiday)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => { setHolidayToDelete(holiday); setDeleteDialogOpen(true); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                        No holidays found for this criteria.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        // Use the length of the filtered/sorted list, not the whole state
                        count={sortedHolidays.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0); // Reset to first page when changing limit
                        }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </TableContainer>
            </Card>

            {/* Add/Edit Dialog */}
            <AppDialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: editMode ? theme.palette.primary.main : theme.palette.success.main, color: 'white' }}>
                    {editMode ? 'Edit Holiday' : 'Add New Holiday'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Holiday Name"
                            fullWidth
                            value={currentHoliday?.name || ''}
                            onChange={(e) => setCurrentHoliday({ ...currentHoliday, name: e.target.value })}
                        />
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                            <DatePicker
                                label="Date"
                                value={currentHoliday?.date || null}
                                onChange={(val) => setCurrentHoliday({ ...currentHoliday, date: val })}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </LocalizationProvider>
                        <FormControl fullWidth>
                            <InputLabel>Applicable Locations</InputLabel>
                            <Select
                                multiple
                                value={currentHoliday?.applicableLocations || []}
                                onChange={handleLocationChange}
                                label="Applicable Locations"
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                                    </Box>
                                )}
                            >
                                <MenuItem value="All">All Locations</MenuItem>
                                {locations.map(loc => (
                                    <MenuItem key={loc.id} value={loc.location_name}>{loc.location_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Description"
                            multiline
                            rows={2}
                            fullWidth
                            value={currentHoliday?.description || ''}
                            onChange={(e) => setCurrentHoliday({ ...currentHoliday, description: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    <Button onClick={() => setDialogOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveHoliday}
                        disabled={loading}
                        color={editMode ? "primary" : "success"}
                        sx={{ textTransform: 'none' }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </AppDialog>

            {/* Delete Confirmation */}
            <AppDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ bgcolor: theme.palette.error.main, color: 'white' }}>Confirm Delete</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography>Are you sure you want to delete <b>{holidayToDelete?.name}</b>?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={loading} sx={{ textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </AppDialog>

            {/* Download Popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Box sx={{ p: 2, minWidth: 150 }}>
                    <Typography variant="subtitle2" gutterBottom>Format</Typography>
                    <RadioGroup value={selectDownloadFormat} onChange={(e) => setSelectDownloadFormat(e.target.value)}>
                        <FormControlLabel value="pdf" control={<Radio size="small" />} label="PDF" />
                        <FormControlLabel value="excel" control={<Radio size="small" />} label="Excel" />
                    </RadioGroup>
                    <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        sx={{ mt: 1 }}
                        onClick={handleDownloadCalendar}
                    >
                        Download
                    </Button>
                </Box>
            </Popover>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HolidayCalendarManager;